import React from 'react';
import { ColorType } from 'consts/colors';
import { GridItem } from '../../../Grid/Grid';
import { Link } from '../../../Link/Link';
import styles from './Module.module.css';
import cx from 'classnames';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

export interface UserFlowProps {
  id: number;
  steps: ModuleProps[];
  title: string;
}
export interface Answer {
  questionResponseCheckboxId: number;
}

export interface AssetProps {
  contentType: string;
  presignedUrl: string;
  s3Key: string;
}

export interface ContentGroupProps {
  iconAsset: AssetProps;
}
export interface ModuleProps {
  description: string;
  index: number;
  isRecommended: boolean;
  contentGroup: ContentGroupProps;
  title: string;
  href: string;
  id: number;
  isCompleted?: boolean;
  isCurrent?: boolean;
  stepTitle: string;
  lessonsToComplete: number;
  lessonsLength: number;
  isPreviousCompleted: boolean;
  isFirst: boolean;
  percentCompleted?: number;
  primaryPagesLength?: number;
  primaryPagesToComplete?: number;
}

const badgeReference = [9, 11, 9, 7];

export function Module({
  description,
  title,
  href,
  isCompleted,
  isFirst,
  primaryPagesLength,
  index,
  primaryPagesToComplete,
  percentCompleted,
  contentGroup,
  isRecommended,
  stepTitle,
  isPreviousCompleted,
  lessonsLength,
  lessonsToComplete,
}: ModuleProps) {
  let iconText = 'Continue';
  let iconColor = '';
  let textColor: ColorType = 'white';
  if (isRecommended) {
    iconText = 'Recommended';
  }
  if (isCompleted) {
    iconText = 'Completed!';
    iconColor = 'green';
  }

  var lessonsIcons = [];
  for (var i = badgeReference[index] ?? primaryPagesLength; i != 0; i--) {
    if (i + 1 < primaryPagesToComplete) {
      lessonsIcons.push(<img className="m-r--x-small m-b--small" src="/images/icon-award-grey.svg" alt="Lesson complete" />);
    } else {
      lessonsIcons.push(<img className="m-r--x-small m-b--small" src="/images/icon-award-green.svg" alt="Lesson incomplete" />);
    }
  }

  const getIconClass = () => {
    switch (index) {
      case 0:
        return 'p-large md-w-12-rem';
      case 1:
        return 'md-w-9-rem p-large  ';
      case 2:
        return 'p-large md-w-9-rem  third-module-icon';
      case 3:
        return 'p-large md-w-10-rem';
    }
  };
  const unlockedActivity = (
    <GridItem sizes={['sml-1', 'med-2', 'lrg-4']}>
      <Link
        className={cx(
          styles['card'],
          isCompleted ? styles['card--completed'] : styles['card--incomplete'],
          isRecommended && styles['card--recommended'],
        )}
        href="/module/[stepUrl]/[pageUrl]"
        as={href}
      >
        <h3 className="h5 h-font--uppercase h-color--navy text-center">{stepTitle}</h3>
        <div className="text-center h-100-px ">
          <img src={`/SVG/module-${index + 1}.svg`} style={{ color: 'grey' }} className={getIconClass()} />
        </div>
        <div className="m-b" style={{ minHeight: '10rem' }}>
          <h4 className="h2">{title}</h4>
          <p>{description}</p>
        </div>

        <h3 className="h6 h-font--uppercase h-color--navy m-t--small">Lessons</h3>
        <div className="l-flex m-b--med flex-wrap ">{lessonsIcons}</div>
        <CircularProgressbar
          value={Math.round(percentCompleted)}
          styles={{
            root: {
              maxWidth: '150px',
              margin: 'auto auto 0 auto',
              overflow: 'visible',
              width: '50%',
            },
            path: {
              stroke: 'var(--green)',
              strokeWidth: 13,
            },
            trail: {
              stroke: 'var(--gray-200)',
              strokeWidth: 13,
            },
            text: {
              fill: 'var(--navy)',
              fontFamily: 'var(--font-secondary)',
              fontSize: '1.2rem',
              fontWeight: 800,
            },
          }}
          text={`${Math.round(percentCompleted)}%`}
        />
        <div className="l-flex align-center justify-center m-t--med">
          <img
            alt={`Module ${isCompleted ? 'complete' : 'incomplete'}`}
            className="m-r--small"
            src={`/images/icon-tick-${isCompleted ? 'green' : 'grey'}.svg`}
          />
          <span className={`${!isCompleted && 'h-color--gray-200'} weight-700`}>{isCompleted ? 'Complete' : 'Incomplete'}</span>
        </div>
      </Link>
    </GridItem>
  );
  const lockedActivity = (
    <GridItem sizes={['sml-1', 'med-2', 'lrg-4']}>
      <div className={cx(styles['card'], isCompleted && styles['card--completed'], isRecommended && styles['card--recommended'])}>
        <h3 className="h5 h-font--uppercase h-color--navy text-center">{stepTitle}</h3>
        <div className="text-center h-100-px ">
          <img src={`/SVG/module-${index + 1}.svg`} style={{ color: 'grey' }} className={getIconClass()} />
        </div>
        <div className="m-b" style={{ minHeight: '10rem' }}>
          <h4 className="h2">{title}</h4>
          <p>{description}</p>
        </div>
        <h3 className="h6 h-font--uppercase h-color--navy m-t--small">Lessons</h3>
        <div className="l-flex m-b--med flex-wrap ">{lessonsIcons}</div>
        <CircularProgressbar
          value={0}
          styles={{
            root: {
              maxWidth: '150px',
              margin: 'auto auto 0 auto',
              overflow: 'visible',
              width: '50%',
            },
            path: {
              stroke: 'var(--green)',
              strokeWidth: 13,
            },
            trail: {
              stroke: 'var(--gray-200)',
              strokeWidth: 13,
            },
            text: {
              fill: 'var(--navy)',
              fontFamily: 'var(--font-secondary)',
              fontSize: '1.2rem',
              fontWeight: 800,
              filter: 'grayscale(1)',
            },
          }}
          text={`0%`}
        />
        <div className="l-flex align-center justify-center m-t--med">
          <img alt={`Module incomplete`} className="m-r--small" src={`/images/icon-tick-grey.svg`} />
          <span className={`h-color--gray-200 weight-700`}>Locked</span>
        </div>
      </div>
    </GridItem>
  );

  return !isFirst && !isPreviousCompleted ? lockedActivity : unlockedActivity;
}
